/**
 * Centro de Operaciones — pantalla ejecutiva diaria (solo frontend, datos existentes).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBoxOpen,
  FaCashRegister,
  FaChartLine,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaShoppingCart,
  FaSync,
  FaWarehouse
} from 'react-icons/fa';

import { useAuth } from '../../contexts/AuthContext';
import dashboardService from '../../services/dashboard.service';
import reportesService from '../../services/reportes.service';
import ventasService from '../../services/ventas.service';
import configuracionService from '../../services/configuracion.service';
import clientesService from '../../services/clientes.service';
import cajaService from '../../services/caja.service';
import productosService from '../../services/productos.service';
import stockSucursalService from '../../services/stock-sucursal.service';
import comprasService from '../../services/compras.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import GananciasFinancieraCard from '../../components/modules/finanzas/GananciasFinancieraCard';
import CoberturaFinancieraCard from '../../components/modules/finanzas/CoberturaFinancieraCard';
import CalidadFinancieraCard from '../../components/modules/finanzas/CalidadFinancieraCard';
import { normalizarDatosDashboard } from '../../utils/normalizarDatosDashboard';
import {
  buildResumenDeudaEjecutivo,
  normalizarDeudaVencidaDias,
  parseCuentasPorCobrarResponse
} from '../../utils/dashboardDeuda';
import {
  buildFilasCrmCuentaCorriente,
  extraerCobrosHoyDesdeResumenPagos
} from '../../utils/crmCuentaCorriente';
import { fechaDiaNegocioLocal } from '../../utils/fechaDiaNegocio';
import {
  ACCIONES_RAPIDAS,
  ESTILOS_PRIORIDAD,
  buildAlertasOperativas,
  buildTarjetasPrioritarias,
  contarClientesVencidosCrm
} from '../../utils/centroOperaciones';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0);

const hoyIso = () => fechaDiaNegocioLocal();

const CentroOperaciones = () => {
  const { orgId, sucursalSeleccionada, isAuthenticated, loadingSucursales, hasPermission } = useAuth();
  const puedeVerGanancias = hasPermission('reportes', 'ver_ganancias');
  const sucursalId = sucursalSeleccionada?.id || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datos, setDatos] = useState(null);

  const cargarDatos = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    const hoy = hoyIso();

    try {
      const [
        metricasSettled,
        configSettled,
        cuentasSettled,
        clientesSettled,
        pagosSettled,
        cajaResumenSettled,
        cierreSettled,
        stockStatsSettled,
        stockBajoSettled,
        comprasSettled,
        finanzasSettled
      ] = await Promise.allSettled([
        dashboardService.obtenerMetricasEjecutivas(),
        configuracionService.obtener(),
        ventasService.obtenerCuentasPorCobrar(),
        clientesService.obtenerTodos(orgId),
        ventasService.obtenerResumenPagos(hoy, hoy),
        sucursalId ? cajaService.obtenerResumen({ fecha: hoy, sucursalId }) : Promise.resolve(null),
        sucursalId ? cajaService.obtenerCierreDia({ fecha: hoy, sucursalId }) : Promise.resolve(null),
        productosService.obtenerEstadisticas(),
        sucursalId ? stockSucursalService.obtenerStockBajo(sucursalId, orgId) : Promise.resolve([]),
        comprasService.obtenerTodas(),
        puedeVerGanancias
          ? reportesService.obtenerFinanzasResumen({ fechaInicio: hoy, fechaFin: hoy, sucursal_id: sucursalId })
          : Promise.resolve(null)
      ]);

      const metricas = metricasSettled.status === 'fulfilled' ? metricasSettled.value : null;
      const config = configSettled.status === 'fulfilled' ? configSettled.value : {};
      const cuentasRaw = cuentasSettled.status === 'fulfilled' ? cuentasSettled.value : null;
      const clientes = clientesSettled.status === 'fulfilled' ? clientesSettled.value : [];
      const resumenPagos = pagosSettled.status === 'fulfilled' ? pagosSettled.value : null;
      const cajaResumen = cajaResumenSettled.status === 'fulfilled' ? cajaResumenSettled.value : null;
      const cierreBody = cierreSettled.status === 'fulfilled' ? cierreSettled.value : null;
      const stockStats = stockStatsSettled.status === 'fulfilled' ? stockStatsSettled.value : {};
      const stockBajoList = stockBajoSettled.status === 'fulfilled' ? stockBajoSettled.value : [];
      const compras = comprasSettled.status === 'fulfilled' ? comprasSettled.value : [];
      const finanzasHoy = finanzasSettled.status === 'fulfilled' ? finanzasSettled.value : null;

      const hoyDash = metricas?.hoy?.ok
        ? metricas.hoy.data
        : (metricas?.raw ? normalizarDatosDashboard(metricas.raw) : null);

      const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
      const deudaBuilt = buildResumenDeudaEjecutivo(parseCuentasPorCobrarResponse(cuentasRaw), diasConfig);
      const filasCrm = buildFilasCrmCuentaCorriente(clientes, cuentasRaw, diasConfig);
      const clientesVencidos = contarClientesVencidosCrm(filasCrm);
      const cobrosHoy = extraerCobrosHoyDesdeResumenPagos(resumenPagos);

      const sinStock = parseInt(stockStats?.sinStock ?? 0, 10) || 0;
      const stockCritico = Array.isArray(stockBajoList)
        ? stockBajoList.length
        : parseInt(hoyDash?.stockBajo ?? stockStats?.stockBajo ?? 0, 10) || 0;

      const cierreCerrado = cierreBody?.cierreCerrado === true || Boolean(cierreBody?.cierre);
      const cierrePendiente = Boolean(sucursalId && !cierreCerrado);

      const comprasPendientes = (compras || []).filter((c) => c.estado === 'pendiente').length;

      const saldoCaja = parseFloat(cajaResumen?.saldo ?? cajaResumen?.data?.saldo ?? 0) || 0;

      setDatos({
        ventasHoy: hoyDash?.ventasHoy ?? 0,
        cantidadVentasHoy: hoyDash?.cantidadVentasHoy ?? 0,
        saldoCaja,
        cajaEstado: cierreCerrado ? 'Cerrada' : (sucursalId ? 'Abierta' : 'Sin sucursal'),
        cobrosHoy,
        stockCritico,
        sinStock,
        clientesVencidos,
        montoVencido: deudaBuilt.resumen?.totalVencido ?? 0,
        totalDeuda: deudaBuilt.resumen?.totalAdeudado ?? 0,
        stockBajoList: Array.isArray(stockBajoList) ? stockBajoList : [],
        cierrePendiente,
        comprasPendientes,
        sucursalNombre: sucursalSeleccionada?.nombre || '—',
        gananciaHoy: finanzasHoy?.resumen?.gananciaBruta ?? hoyDash?.gananciasHoy ?? 0,
        finanzasCobertura: finanzasHoy?.cobertura || null,
        finanzasCalidad: finanzasHoy?.calidadFinanciera || null,
        finanzasAlertas: finanzasHoy?.alertas || [],
        finanzasActualizadoEn: metricas?.gananciasMes?.actualizadoEn || new Date().toISOString()
      });
    } catch (err) {
      console.error('[CentroOperaciones]', err);
      setError(err?.message || 'No se pudo cargar el centro de control');
      setDatos(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, sucursalId, sucursalSeleccionada?.nombre, puedeVerGanancias]);

  useEffect(() => {
    if (!isAuthenticated || loadingSucursales) return;
    if (!orgId) return;
    cargarDatos();
  }, [isAuthenticated, loadingSucursales, orgId, cargarDatos]);

  const tarjetas = useMemo(() => {
    if (!datos) return [];
    return buildTarjetasPrioritarias(datos);
  }, [datos]);

  const alertas = useMemo(() => {
    if (!datos) return [];
    return buildAlertasOperativas({
      ...datos,
      productosStockBajo: datos.stockBajoList
    });
  }, [datos]);

  const fechaLabel = useMemo(() => {
    try {
      return new Date(`${hoyIso()}T12:00:00`).toLocaleDateString('es-AR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    } catch {
      return hoyIso();
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <Spinner size="lg" />
        <span className="ml-3 text-e0e3-muted">Preparando centro de control…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-e0e3-muted capitalize">{fechaLabel}</p>
          <h1 className="text-2xl font-bold text-e0e3-foreground flex items-center gap-2 mt-1">
            <FaClipboardCheck className="text-e0e3-accent" />
            Centro de Control
          </h1>
          <p className="text-sm text-e0e3-muted mt-1">
            Detectá problemas y resolvé lo importante desde un solo lugar · {datos?.sucursalNombre || 'Sucursal'}
          </p>
        </div>
        <Button color="secondary" icon={<FaSync />} onClick={cargarDatos}>
          Actualizar
        </Button>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          {error}
          <div className="mt-3">
            <Button size="sm" color="secondary" onClick={cargarDatos}>Reintentar</Button>
          </div>
        </div>
      ) : null}

      {/* 1. Resumen de hoy */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-e0e3-muted mb-3">
          Resumen de hoy
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5">
            <p className="text-xs font-medium text-e0e3-muted flex items-center gap-1.5">
              <FaShoppingCart className="text-e0e3-accent" /> Ventas de hoy
            </p>
            <p className="text-2xl font-bold text-e0e3-foreground mt-2">
              {formatMoney(datos?.ventasHoy)}
            </p>
            <p className="text-xs text-e0e3-muted mt-1">
              {datos?.cantidadVentasHoy ?? 0} operación{(datos?.cantidadVentasHoy ?? 0) === 1 ? '' : 'es'}
            </p>
          </div>
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5">
            <p className="text-xs font-medium text-e0e3-muted flex items-center gap-1.5">
              <FaCashRegister className="text-e0e3-accent" /> Caja
            </p>
            <p className="text-2xl font-bold text-e0e3-foreground mt-2">
              {formatMoney(datos?.saldoCaja)}
            </p>
            <p className="text-xs text-e0e3-muted mt-1">Estado: {datos?.cajaEstado || '—'}</p>
          </div>
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5">
            <p className="text-xs font-medium text-e0e3-muted flex items-center gap-1.5">
              <FaFileInvoiceDollar className="text-e0e3-accent" /> Cobros CC hoy
            </p>
            <p className="text-2xl font-bold text-emerald-300 mt-2">
              {typeof datos?.cobrosHoy === 'number' && datos.cobrosHoy >= 1000
                ? formatMoney(datos.cobrosHoy)
                : (datos?.cobrosHoy ?? 0)}
            </p>
            <p className="text-xs text-e0e3-muted mt-1">
              {typeof datos?.cobrosHoy === 'number' && datos.cobrosHoy > 0 && datos.cobrosHoy < 1000
                ? 'Pagos registrados'
                : 'Cuenta corriente'}
            </p>
          </div>
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5">
            <p className="text-xs font-medium text-e0e3-muted flex items-center gap-1.5">
              <FaWarehouse className="text-e0e3-accent" /> Stock crítico
            </p>
            <p className={`text-2xl font-bold mt-2 ${(datos?.stockCritico ?? 0) > 0 ? 'text-orange-300' : 'text-e0e3-foreground'}`}>
              {datos?.stockCritico ?? 0}
            </p>
            <p className="text-xs text-e0e3-muted mt-1">
              {(datos?.sinStock ?? 0) > 0 ? `${datos.sinStock} sin stock` : 'Bajo mínimo sucursal'}
            </p>
          </div>
        </div>
      </section>

      {puedeVerGanancias && datos?.finanzasCobertura && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-e0e3-muted mb-3 flex items-center gap-2">
            <FaChartLine className="text-e0e3-accent" /> Finanzas (hoy)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <GananciasFinancieraCard
              titulo="Ganancia bruta"
              valor={formatMoney(datos.gananciaHoy)}
              cobertura={datos.finanzasCobertura}
              calidadFinanciera={datos.finanzasCalidad}
              actualizadoEn={datos.finanzasActualizadoEn}
              compact
            />
            <CoberturaFinancieraCard cobertura={datos.finanzasCobertura} compact />
            {datos.finanzasCalidad && (
            <CalidadFinancieraCard
              calidadFinanciera={datos.finanzasCalidad}
              cobertura={datos.finanzasCobertura}
              compact
            />
            )}
          </div>
          {datos.finanzasAlertas?.length > 0 && (
            <div className="mt-4 space-y-2">
              {datos.finanzasAlertas.map((alerta) => (
                <div
                  key={alerta.tipo}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
                >
                  <FaExclamationTriangle />
                  <span>{alerta.mensaje}</span>
                  {alerta.enlace && (
                    <Link to={alerta.enlace} className="font-semibold underline">
                      {alerta.tipo === 'productos_sin_costo'
                        ? 'Ver productos sin costo registrado'
                        : 'Ver detalle'}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 2. Acciones prioritarias */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-e0e3-muted mb-3">
          Acciones prioritarias
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tarjetas.map((t) => {
            const est = ESTILOS_PRIORIDAD[t.prioridad] || ESTILOS_PRIORIDAD.verde;
            return (
              <Link
                key={t.id}
                to={t.to}
                className={`rounded-xl border p-4 transition-colors hover:brightness-110 ${est.card}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${est.badge}`}>
                      {t.prioridad}
                    </span>
                    <p className="font-semibold text-e0e3-foreground mt-2">{t.titulo}</p>
                    <p className="text-xs text-e0e3-muted mt-1">{t.detalle}</p>
                  </div>
                  <p className="text-xl font-bold text-e0e3-foreground tabular-nums shrink-0">
                    {t.monto != null && t.monto > 0 ? formatMoney(t.monto) : t.valor}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 3. Alertas operativas */}
      <Card title="Alertas operativas" icon={<FaBoxOpen />}>
        {alertas.length === 0 ? (
          <p className="text-sm text-e0e3-muted text-center py-6">Sin alertas por ahora.</p>
        ) : (
          <ul className="divide-y divide-e0e3-border/20">
            {alertas.map((a) => {
              const est = ESTILOS_PRIORIDAD[a.prioridad] || ESTILOS_PRIORIDAD.verde;
              return (
                <li key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${est.dot}`} aria-hidden />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-e0e3-foreground">{a.titulo}</p>
                    <p className="text-xs text-e0e3-muted mt-0.5">{a.impacto}</p>
                  </div>
                  <Link to={a.to} className="shrink-0">
                    <Button size="sm" color="secondary" icon={<FaArrowRight />}>
                      {a.accion}
                    </Button>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* 4. Acciones rápidas */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-e0e3-muted mb-3">
          Acciones rápidas
        </h2>
        <div className="flex flex-wrap gap-2">
          {ACCIONES_RAPIDAS.map((acc) => (
            <Link key={acc.id} to={acc.to}>
              <Button color={acc.primary ? 'primary' : 'secondary'} size="sm">
                {acc.label}
              </Button>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CentroOperaciones;
